import { useAuth } from "../auth.js";
 import { AdminBreadcrumb } from "./AdminHome.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import {
  registryOfficeInputSchema,
  registryOfficeUpdateSchema,
  type BulkImportResult,
} from "@titleverify/shared";
import { apiErrorText, apiFetch, type RegistryOffice } from "../api.js";

/**
 * The operator's registry-office management screen (CLAUDE.md §2A). A
 * non-technical operator fills and manages the routing table here — no migration,
 * no redeploy — while every record passes the SAME validation as the API. The
 * form validates against `registryOfficeInputSchema`, the exact schema the server
 * parses, so a bad row is caught here first and refused there regardless.
 *
 * Nothing here fabricates or hides. The empty state says the table is empty; a
 * bulk import shows the honest per-row result, failures and all.
 *
 * All four server doors are reachable from here — create, import, correct
 * (PATCH), deactivate — because §2A means an operator's typo in a routing key
 * must be fixable by the operator. `code` is the primary key and the FK a routed
 * case points at, so it is the one field an edit cannot touch; the server's
 * update schema omits it and so does the form.
 */
export function AdminRegistry() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  // Which row's edit form is open. One at a time: two half-finished corrections
  // to the routing table on screen at once is how the wrong one gets saved.
  const [editing, setEditing] = useState<string | null>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ["admin", "registry-offices"],
    queryFn: async () =>
      apiFetch<{ registryOffices: RegistryOffice[] }>("/admin/registry-offices", {
        token: await getToken(),
      }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "registry-offices"] });

  if (isPending) return <p className="muted" role="status">Loading the registry…</p>;
  if (error) return <p role="alert">{apiErrorText(error, "Could not load the registry offices.")}</p>;

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Jurisdiction table</p>
        <h2>Registry offices</h2>
        <p className="lede">
          The jurisdiction table routing keys off. Add offices from an authoritative
          source (the Registrar General's Department) — each row records who entered
          it and from where. An empty table routes nothing, on purpose.
        </p>
      </header>

      <CreateForm getToken={getToken} onDone={invalidate} />
      <ImportPanel getToken={getToken} onDone={invalidate} />

      {data.registryOffices.length === 0 ? (
        <p className="empty">No registry offices yet. Add one above, or import a list.</p>
      ) : (
        <div className="table-wrap" tabIndex={0} role="region" aria-label="Registry offices">
          <table>
            <thead>
              <tr>
                {["Code", "Name", "District", "Active", "Source", ""].map((h) => (
                  <th key={h}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.registryOffices.map((o) => (
                <Fragment key={o.code}>
                  <tr>
                    <td>
                      <code>{o.code}</code>
                    </td>
                    <td>{o.name}</td>
                    <td>
                      <DistrictCell district={o.district} active={o.active} />
                    </td>
                    <td>
                      {/*
                        Neutral in both directions on purpose. An office being
                        active or not is a fact about the routing table, not good
                        or bad news, so it gets no colour with an opinion in it.
                      */}
                      <span className="status status-neutral">{o.active ? "yes" : "no"}</span>
                    </td>
                    <td>{o.source}</td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="quiet compact"
                          aria-expanded={editing === o.code}
                          aria-controls={`edit-${o.code}`}
                          onClick={() => setEditing((c) => (c === o.code ? null : o.code))}
                        >
                          {editing === o.code ? "Close" : "Edit"}
                        </button>
                        {o.active ? (
                          <DeactivateButton code={o.code} getToken={getToken} onDone={invalidate} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {editing === o.code ? (
                    <tr>
                      <td colSpan={6} id={`edit-${o.code}`}>
                        <EditForm
                          office={o}
                          getToken={getToken}
                          onDone={() => {
                            invalidate();
                            setEditing(null);
                          }}
                          onCancel={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type TokenFn = { getToken: () => Promise<string | null> };

/**
 * The district cell.
 *
 * District is not decoration — it is the key `workflow/routing.ts` matches a
 * parcel's district against, exactly, alongside `active = true`. So an office
 * with no district cannot be resolved for any parcel, ever. A bare em-dash said
 * that in a way that reads as an empty cell rather than as a stated absence with
 * a consequence, so it now says both: that nothing was recorded, and what that
 * costs. The consequence is only claimed where it is the operative one — for an
 * inactive office, routing already skips it and the missing district is not the
 * reason it cannot be matched.
 */
function DistrictCell({ district, active }: { district: string | null; active: boolean }) {
  if (district) return <>{district}</>;
  return (
    <>
      <strong>Not recorded</strong>
      <small className="field-hint">
        {active
          ? "Routing matches a parcel's district against this field, so no case can be routed to this office until it is recorded. Edit the office to set it."
          : "Never recorded. This office is inactive, so it is out of routing either way."}
      </small>
    </>
  );
}

/**
 * The correction door (§2A). PATCH /admin/registry-offices/:code exists on the
 * server and had no UI, so a typo in a routing key's name, district or source
 * needed a developer and a database to fix — the exact thing §2A forbids.
 *
 * The fields offered are precisely the ones `registryOfficeUpdateSchema` accepts
 * (`registryOfficeInputSchema` minus `code`, partial): name, district, province,
 * active, source. `code` is absent because it is the primary key and the FK a
 * routed case references; the server will not change it and this form does not
 * pretend otherwise.
 *
 * Provenance, per §2A's "an edit that loses provenance is the hole": the server
 * stamps `sourced_by` = the acting admin and `sourced_at` = now on every update
 * (registry/service.ts), which is the who and the when. The what-from is
 * `source`, and this form sends it on every save rather than leaving it out of
 * the patch — a row must not end up stamped with a corrector who never restated
 * where the corrected value came from. The field is pre-filled with the current
 * source so restating it is one keystroke when it has not changed, and the schema
 * refuses it empty.
 */
function EditForm({
  office,
  getToken,
  onDone,
  onCancel,
}: TokenFn & { office: RegistryOffice; onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    name: office.name,
    district: office.district ?? "",
    province: office.province ?? "",
    source: office.source,
    active: office.active,
  });
  const [clientError, setClientError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: async (body: unknown) =>
      apiFetch<RegistryOffice>(`/admin/registry-offices/${encodeURIComponent(office.code)}`, {
        token: await getToken(),
        method: "PATCH",
        body: body as Record<string, unknown>,
      }),
    onSuccess: onDone,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setClientError(null);
    // The SAME schema the server parses this PATCH with. Every field is sent, so
    // `.partial()` grants no leniency here: an emptied name, district or source
    // fails the shared rules at the form and would be refused at the server too.
    // Province is the one optional field, and it is sent even when the operator
    // clears it — omitting it would mean "leave unchanged", i.e. quietly discard
    // an edit they made, which is the silent failure §7 forbids.
    const parsed = registryOfficeUpdateSchema.safeParse({
      name: form.name,
      district: form.district,
      province: form.province,
      active: form.active,
      source: form.source,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setClientError(issue ? `${issue.path.join(".")}: ${issue.message}` : "invalid");
      return;
    }
    update.mutate(parsed.data);
  }

  const activeChanged = form.active !== office.active;

  return (
    <form onSubmit={submit}>
      <fieldset>
        <legend>Edit {office.code}</legend>
        <p className="muted">
          The code is the routing key routed cases point at and cannot be changed.
          An office recorded under the wrong code is deactivated and re-added.
        </p>

        {(["name", "district", "province", "source"] as const).map((field) => (
          <div className="field" key={field}>
            <label htmlFor={`edit-${office.code}-${field}`}>
              {field}
              {field === "province" ? "" : " *"}
            </label>
            <input
              type="text"
              id={`edit-${office.code}-${field}`}
              value={form[field]}
              onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
            />
            {field === "district" ? (
              <small className="field-hint">
                Matched against a parcel's district exactly, and only for an active office.
              </small>
            ) : null}
            {field === "source" ? (
              <small className="field-hint">
                Where this version of the row comes from. Saving also records you as the
                person who last sourced it, and the time you saved.
              </small>
            ) : null}
          </div>
        ))}

        <div className="field">
          <label htmlFor={`edit-${office.code}-active`}>active</label>
          <select
            id={`edit-${office.code}-active`}
            value={form.active ? "yes" : "no"}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === "yes" }))}
          >
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </div>

        {/*
          What saving does, before it is confirmed — and only when the answer is
          actually changing. A reactivation is the one edit here that puts an
          office back in front of live routing, so it says so in its own words.
        */}
        {activeChanged ? (
          <p className="consequence">
            {form.active
              ? "Saving puts this office back into routing: new cases whose parcel matches its district can be routed to it again."
              : "Saving takes this office out of routing: no new case can be routed to it. Cases already routed to it keep their record and are not moved."}
          </p>
        ) : null}

        {clientError ? <p role="alert">{clientError}</p> : null}
        {update.error ? (
          <p role="alert">{apiErrorText(update.error, "Could not save this office.")}</p>
        ) : null}

        <div className="actions">
          <button type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
          <button type="button" className="quiet" onClick={onCancel} disabled={update.isPending}>
            Cancel
          </button>
        </div>
      </fieldset>
    </form>
  );
}

function CreateForm({ getToken, onDone }: TokenFn & { onDone: () => void }) {
  const [form, setForm] = useState({ code: "", name: "", district: "", province: "", source: "" });
  const [clientError, setClientError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (body: unknown) =>
      apiFetch<RegistryOffice>("/admin/registry-offices", {
        token: await getToken(),
        method: "POST",
        body: body as Record<string, unknown>,
      }),
    onSuccess: () => {
      setForm({ code: "", name: "", district: "", province: "", source: "" });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setClientError(null);
    // The SAME schema the server uses — caught here first, refused there regardless.
    const parsed = registryOfficeInputSchema.safeParse({
      ...form,
      province: form.province || undefined,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setClientError(issue ? `${issue.path.join(".")}: ${issue.message}` : "invalid");
      return;
    }
    create.mutate(parsed.data);
  }

  return (
    <form onSubmit={submit}>
      <fieldset>
        <legend>Add an office</legend>
        {(["code", "name", "district", "province", "source"] as const).map((field) => (
          <Fragment key={field}>
            <label htmlFor={`office-${field}`}>
              {field}
              {field === "province" ? "" : " *"}
            </label>
            <input
              type="text"
              id={`office-${field}`}
              value={form[field]}
              onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
            />
          </Fragment>
        ))}
        {clientError ? <p role="alert">{clientError}</p> : null}
        {/*
          apiErrorText, not `(error as ApiError).message`. That cast printed the
          API's machine code at the operator — a zod 400 arrives as
          "invalid_request", which tells someone filling a routing table nothing
          about which field they got wrong — and it lied about the type: a
          dropped connection rejects with a TypeError that has no `.status` and
          no `.fromApi`, and casting it to ApiError does not make it one. The
          edit form one component down already did this correctly; the two now
          agree.
        */}
        {create.error ? (
          <p role="alert">{apiErrorText(create.error, "Could not add this office.")}</p>
        ) : null}
        <div className="actions">
          <button type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add office"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

function ImportPanel({ getToken, onDone }: TokenFn & { onDone: () => void }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const importer = useMutation({
    mutationFn: async (rows: unknown[]) =>
      apiFetch<BulkImportResult>("/admin/registry-offices/import", {
        token: await getToken(),
        method: "POST",
        body: { rows },
      }),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
    // Refreshed on failure too: a part-completed import leaves rows behind, and
    // the operator has to be able to see which. See the alert below.
    onError: onDone,
  });

  function run() {
    setParseError(null);
    setResult(null);
    let rows: unknown;
    try {
      rows = JSON.parse(text);
    } catch {
      setParseError("Paste a JSON array of rows.");
      return;
    }
    if (!Array.isArray(rows)) {
      setParseError("The import must be a JSON array.");
      return;
    }
    importer.mutate(rows);
  }

  return (
    /*
      No className. This carried `import-panel`, which was never defined in
      styles.css — the box, the padding and the uppercase summary all come from
      the bare `details`/`summary` element rules, which were written for exactly
      this element ("today the bulk importer", styles.css). The class therefore
      contributed nothing and only implied a style rule a reader would go looking
      for and not find. Removed rather than defined: adding an empty definition
      to justify a name is the same lie in the other file.
    */
    <details>
      <summary>Bulk import (JSON array)</summary>
      <div className="field">
        <label htmlFor="import-rows" className="visually-hidden">
          Bulk import (JSON array)
        </label>
        <textarea
          id="import-rows"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder='[{ "code": "COL-01", "name": "Colombo Land Registry", "district": "Colombo", "source": "RGD gazette 2026/14" }]'
        />
      </div>
      {parseError ? <p role="alert">{parseError}</p> : null}
      {/*
        The import request FAILING, as distinct from importing with failed rows.
        This was rendered nowhere: `onSuccess` sets the result, so a 403, a 500
        or a dropped connection left the panel exactly as it was — the operator
        pressed Import, the button un-disabled itself, and nothing appeared. They
        cannot tell that from an import that did nothing, which is the silent
        failure CLAUDE.md §7 forbids.

        What it must NOT say is "nothing was imported". The server's importer
        inserts each row in its OWN transaction, precisely so one bad row cannot
        roll back a good one (registry/service.ts) — so a request that failed
        partway may well have written some of them, and this browser was never
        told how many. Claiming either number here would be inventing the one
        fact the platform did not receive. It says what is true: the outcome is
        unknown, and the table below is the authority. `onError` refreshes that
        table so the operator is reading what actually landed rather than the
        list from before they pressed the button.
      */}
      {importer.error ? (
        <p role="alert">
          {apiErrorText(importer.error, "The import did not finish.")} Rows are imported one at a
          time, so some may have been written before it stopped — this page was not told which. The
          table below has been reloaded and is what the registry now holds; check it before
          importing the same list again.
        </p>
      ) : null}
      <div className="actions">
        <button type="button" onClick={run} disabled={importer.isPending}>
          {importer.isPending ? "Importing…" : "Import"}
        </button>
      </div>

      {/*
        Honest per-row outcome — the good rows landed; every failure is named.
        Rendered as a neutral .outcome rather than an alert: a partial import is
        a result the operator asked for, and the failed rows below say plainly
        which ones did not land. Dressing the whole thing in the danger colours
        would report the successful rows as a failure too.
      */}
      {result ? (
        <div className="outcome">
          <p>
            Imported {result.inserted}. Failed {result.failed.length}.
          </p>
          {result.failed.length > 0 ? (
            <ul>
              {result.failed.map((f) => (
                <li key={f.index}>
                  row {f.index}: {f.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

function DeactivateButton({ code, getToken, onDone }: TokenFn & { code: string; onDone: () => void }) {
  const deactivate = useMutation({
    mutationFn: async () =>
      // encodeURIComponent, as the PATCH one component up already does. A code is
      // not path-safe by default: `registryOfficeInputSchema` permits slashes
      // ("uppercase letters, digits, hyphens or slashes"), so an office recorded
      // as COL/01 built .../registry-offices/COL/01/deactivate — a different
      // route, and a 404 the operator would have read as the office being gone.
      apiFetch<RegistryOffice>(
        `/admin/registry-offices/${encodeURIComponent(code)}/deactivate`,
        { token: await getToken(), method: "POST" },
      ),
    onSuccess: onDone,
  });
  return (
    <>
      {/*
        .compact to match the Edit button beside it — this is a control inside a
        table row, and it was rendering at full form-button size next to one that
        was not. Not .danger: deactivation takes an office out of routing and the
        edit form puts it back, so it is not the irreversible act that colour is
        reserved for.
      */}
      <button
        type="button"
        className="compact"
        onClick={() => deactivate.mutate()}
        disabled={deactivate.isPending}
      >
        {deactivate.isPending ? "Deactivating…" : "Deactivate"}
      </button>
      {/*
        The failure was rendered nowhere at all. `onSuccess` refreshes the list,
        so a refused deactivation simply left the row saying "active: yes" with
        no explanation — indistinguishable from a click that never registered,
        and the operator's next move would be to press it again. The server's own
        reason is printed, and the row's unchanged state is confirmed rather than
        left to be inferred.
      */}
      {deactivate.error ? (
        <p role="alert">
          {apiErrorText(deactivate.error, "Could not deactivate this office.")} It is still active
          and still in routing.
        </p>
      ) : null}
    </>
  );
}
